<template>
	<datatable
:headers :value="computedItems" selectable expandable container-class="min-h-[500px]"
		:filter-options="filterOptions" :global-filter-fields="filterFields" :body-row-class-name="(item: ContractItem) =>
				`${statusParam(item, 'hire_desk_complete', 'pick') ? 'actioned ' : ''}
				${!item.selectable ? 'not-selectable ' : ''}`
			" :loading="loading" @update:items-selected="(selections) => (selected = selections)">
		<template #filter-picking_reference="{ model, callback }">
			<app-dropdown
v-if="model" v-model="model.value" label-key="value" size="small" :options="pickRefOptions"
				@update:model-value="callback()">
				Pick Ref
			</app-dropdown>
		</template>

		<template #filter-delivery_method="{ model, callback }">
			<app-dropdown
v-if="model" v-model="model.value" label-key="value" size="small" :options="deliveryMethods"
				@update:model-value="callback()">
				Delivery Method
			</app-dropdown>
		</template>

		<template #filter-depot="{ model, callback }">
			<app-dropdown
v-if="model" v-model="model.value" label-key="value" size="small"
				:options="depotFilterOptions" @update:model-value="callback()">
				Depot
			</app-dropdown>
		</template>

		<template #row-desc="{ row }">
			<div v-if="row.type !== 'Component'">
				{{ row.desc }}
			</div>
			<div v-else>
				<span class="ml-3">{{ `&#x2022; ${row.desc}` }}</span>
			</div>
		</template>

		<template #row-stock_status="{ row }">
			<div v-if="statusParam(row, 'status', 'pick')" class="flex items-center mt-2 mb-2">
				<app-badge class="gap-2" size="small" :theme="getStatusClass(statusParam(row, 'status', 'pick'))">
					{{ statusParam(row, 'status', 'pick') }}
				</app-badge>
			</div>
		</template>

		<template #row-pick_status="{ row }">
			{{ statusParam(row, 'pick_number', 'pick') }}
		</template>

		<template #row-quantity_confirmed="{ row }">
			{{ statusParam(row, 'quantity_confirmed', 'pick') }}
		</template>

		<template #row-quantity_loaded="{ row }">
			{{ statusParam(row, 'quantity_loaded', 'pick') }}
		</template>

		<template #row-delivery_method="{ row }">
			<div class="font-semibold">
				{{ statusParam(row, 'delivery_method', 'pick') }}
			</div>
			<div>
				{{ statusParam(row, 'delivery_method_name', 'pick') }}
			</div>
		</template>

		<template #row-depot="{ row }">
			<span v-if="statusParam(row, 'depot', 'pick')">
				{{ statusParam(row, 'depot', 'pick') }}
			</span>

			<span v-else>
				{{ row.depot }}
			</span>
		</template>

		<template #row-unique_items="{ row }">
			<div class="flex items-center">
				{{
					statusParam(row, 'item_picked', 'pick') &&
						statusParam(row, 'item_picked', 'pick').length > 0
						? statusParam(row, 'item_picked', 'pick')[0]
						: ''
				}}

				<button
v-if="
					statusParam(row, 'item_picked', 'pick') &&
					statusParam(row, 'item_picked', 'pick').length > 1
				" class="rounded-sm bg-mabey-950 text-white px-1 py-[2px] ms-2"
					@click="uniqueItems = statusParam(row, 'item_picked', 'pick')">
					+{{ statusParam(row, 'item_picked', 'pick').length - 1 }}
				</button>
			</div>
		</template>

		<template #row-tos="{ row }">
			<app-checkbox
v-if="row.pick_status && row.pick_status.length > 0" v-model="row.pick_status[0].tos"
				wrapper="" name="tos" :true-value="1" :false-value="0"
				@update:model-value="emit('updatedItems', row)" />
		</template>

		<template #row-actioned="{ row }">
			<app-checkbox
v-if="row.pick_status && row.pick_status.length > 0"
				v-model="row.pick_status[0].hire_desk_complete" wrapper="" name="hire_desk_complete" :true-value="1"
				:false-value="0" @update:model-value="emit('updatedItems', row)" />
		</template>

		<template #row-notes="{ row }">
			<notes-button
v-if="row.pick_status && row.pick_status[0]" :status="row.pick_status[0]"
				@click="editItem(row)" />
		</template>
	</datatable>

	<edit-contract-item-note
:item="editing" area="pick" :resolve-allowed="true" :resolve-on-save="true"
		@updated="emit('updatedItems')" @closed="editing = undefined" />

	<Confirm
:open="uniqueItems !== null" title="Unique Items" theme="info" button="Done" :cancel="false"
		@confirmed="uniqueItems = null">
		{{ uniqueItems?.join(', ') }}
	</Confirm>
</template>

<script lang="ts" setup>
import type { Header } from '@/data/types/datatable'
import { ref, watch, computed } from 'vue'
import { ContractItem } from '@/data/schema/contract-item'
import { ContractItemStatus } from '@/data/schema/contract-item-status'
import EditContractItemNote from '@/components/partials/cat2/EditContractItemNote.vue'
import { getStatusClass } from '@/composables/datatable'
import useContracts, { statusParam } from '@/composables/contracts'
import NotesButton from '@/components/partials/cat2/NotesButton.vue'
import Confirm from '@/components/partials/dialogs/Confirm.vue'
import { FilterMatchMode } from '@primevue/core/api'

const props = defineProps({
	items: {
		type: Array<ContractItem>,
		default: []
	},
	pickRefs: Boolean,
	loading: Boolean
})

const editing = ref<ContractItem | undefined>(undefined)
const uniqueItems = ref<string[] | null>(null)
const selected = ref<ContractItem[]>([])

// Filtering and options
const { getPickingRefOptions, getDeliveryMethodOptions, getDepotOptions } = useContracts()
const pickRefOptions = ref([])
const deliveryMethods = ref([])
const depotFilterOptions = ref([])

const filterOptions = ref({
	picking_reference: { value: null, matchMode: FilterMatchMode.EQUALS },
	delivery_method: { value: null, matchMode: FilterMatchMode.EQUALS },
	depot: { value: null, matchMode: FilterMatchMode.EQUALS }
})

const filterFields = computed(() => {
	let fields = []

	if (pickRefOptions.value.length > 1) {
		fields.push('picking_reference')
	}

	if (deliveryMethods.value.length > 1) {
		fields.push('delivery_method')
	}

	if (depotFilterOptions.value.length > 1) {
		fields.push('depot')
	}

	return fields
})

// Keep track of the first selected items depot
// To ensure only items at that depot can be selected
const selectedDepot = computed(() => {
	if (selected.value.length == 0) {
		return null
	}

	// check for status depot value first
	if (selected.value[0].pick_status && selected.value[0].pick_status.length > 0) {
		return selected.value[0].pick_status?.[0].depot ?? selected.value[0].depot
	}

	return selected.value[0].depot
})

// Move data to top layer for client side filtering
const computedItems = computed(() => {
	return props.items.map((item) => {
		const newItem = {
			...item,
			delivery_method: item.pick_status?.[0]?.delivery_method ?? null,
			selectable: isSelectable(item)
		}

		if (item.kit_components) {
			newItem.kit_components = item.kit_components.map((component) => ({
				...component,
				selectable: isSelectable(component)
			}))
		}

		return newItem
	})
})

const headers = ref<Header[]>([
	{ value: 'stock_status', text: 'Status', props: { width: 120 } },
	{ value: 'item_number', text: 'Item No', props: { sortable: true, width: 120 } },
	{ value: 'description', text: 'Item Description', props: { sortable: true, width: 250 } },
	{ value: 'type', text: 'Type', props: { sortable: true, width: 100 } },
	{ value: 'quantity_required', text: 'Qty Req', props: { sortable: true } },
	{ value: 'quantity_confirmed', text: 'Qty Picked', props: { sortable: true } },
	{ value: 'quantity_loaded', text: 'Qty Load', props: { sortable: true } },
	{ value: 'weight', text: 'Weight', props: { sortable: true, width: 100 } },
	{ value: 'pick_status', text: 'Pick Number', props: { sortable: true, width: 100 } },
	{ value: 'unique_items', text: 'Unique Items', props: { sortable: false } },
	{
		value: 'delivery_method',
		text: 'Del Method',
		props: { sortable: true, showFilterMenu: false }
	},
	{ value: 'depot', text: 'Depot', props: { sortable: true, showFilterMenu: false } },
	{ value: 'tos', text: 'Direct to Site', props: { sortable: false } },
	{ value: 'actioned', text: 'Actioned', props: { sortable: false } },
	{ value: 'notes', text: 'Notes', props: { sortable: false } }
])

// Conditionally add picking ref column
const pickingRefCol = {
	value: 'picking_reference',
	text: 'Pick Ref',
	props: {
		sortable: true,
		showFilterMenu: false
	}
}
if (props.pickRefs) {
	headers.value.splice(2, 0, pickingRefCol)
}

// Events
const emit = defineEmits(['updatedItems', 'selectedItems', 'selectedDepot'])

/**
 * Ensure a note param exists
 * Set editing value to open slideover
 * @param item
 */
function editItem(item: ContractItem) {
	if (!item.pick_status || !item.pick_status[0]) {
		item.pick_status = []
		item.pick_status[0] = new ContractItemStatus()
	}

	if (!item.pick_status[0].notes) {
		item.pick_status[0].notes = ''
	}

	editing.value = item
}

function isSelectable(item: ContractItem) {
	if (!item.pick_status) {
		return false
	}

	// If user has used select-all, we need to check if
	// item has already been selected, to enable deselecting
	const selectionIds = selected.value.map((selection) => selection.id)
	if (selectionIds.includes(item.id)) {
		return true
	}

	const itemDepot = item.pick_status?.[0]?.depot ?? item.depot
	const complete = statusParam(item, 'status', 'pick') == 'Complete'
	const inProgress = statusParam(item, 'status', 'pick') == 'Load In Progress'
	const validDepot = !selectedDepot.value || itemDepot == selectedDepot.value

	return (complete || inProgress) && validDepot
}

watch(
	() => selected.value,
	() => {
		emit('selectedItems', selected.value)
		emit('selectedDepot', selectedDepot.value)
	}
)

watch(
	() => props.loading,
	(newValue) => {
		if (!newValue) {
			selected.value = []
			pickRefOptions.value = getPickingRefOptions(props.items)
			deliveryMethods.value = getDeliveryMethodOptions(props.items, 'pick')
			depotFilterOptions.value = getDepotOptions(props.items, 'pick_status')
		}
	}
)
</script>
